import _ from "lodash"
import { Box, Typography } from "@mui/material"
import { SUBTLE } from "../../styles/foundation/colors"
import RHFSelectField from "./RHFSelectField"
import RHFSearchableSelectField from "./RHFSearchableSelectField"
import RHFMultipleSelectField from "./RHFMultipleSelectField"
import { apiConstants, getGeneric, postGeneric } from "../constants"
import { useEffect, useMemo } from "react"
import { useState } from "react"
import { useFormContext } from "react-hook-form"
import useGetCourier from "../../components/dashboard/performance/useGetCourier"
import useGetCourierCached from "../../components/dashboard/performance/useGetCourierCached"
import { useGetAllCourierPartnersQuery } from "../../redux/commonRTK"

export default function CourierFilter({ enableNoneSelect = false }) {
    const { data } = useGetCourier()

    return (
        <RHFSelectField
            enableNoneSelect={enableNoneSelect}
            width="100%"
            name="courier"
            label={`Select Courier`}
            options={
                _.orderBy(data, 'name').map((val) => ({
                    value: val.id,
                    label: val.parent
                }))
            }
        />
    )

}
// Sentinel value for the Service Type "select/deselect everything under this courier" option.
export const ALL_SERVICE_TYPES = '__ALL_SERVICE_TYPES__'
const ALL_SERVICE_TYPES_OPTION = { label: 'All Service Types', value: ALL_SERVICE_TYPES }

// Courier dropdown for the label/invoice bulk download pages, showing the courier BRAND
// name (e.g. "XpressBees"), not a specific account code. Several courier accounts (e.g.
// different weight slabs) can share the same parent — pick the parent here, then narrow to
// a Service Type via CourierServiceTypeFilter. `couriers` is the merchant's allocated-couriers
// list (see useMerchantAllocatedCouriers), shared with CourierServiceTypeFilter so both
// dropdowns come from one fetch.
export function CourierParentFilter({ couriers = [], merchantId, enableNoneSelect = true, hideNoneHint = false, label = 'Courier' }) {
    const options = useMemo(
        () => _.orderBy(_.uniq(couriers.map((c) => c.parent).filter(Boolean))).map((p) => ({ value: p, label: p })),
        [couriers]
    )

    return (
        <RHFSearchableSelectField
            enableNoneSelect={enableNoneSelect}
            hideNoneHint={hideNoneHint}
            width="100%"
            name="courierParent"
            label={label}
            placeholder="Search allocated couriers"
            noOptionsText={merchantId ? 'No couriers allocated' : 'No merchant selected'}
            options={options}
        />
    )
}

// Service Type multi-select, cascading off the courier selected via CourierParentFilter
// (`parent`). Options are the distinct serviceTypePrimary/serviceTypeSecondary values across
// that parent's accounts, derived from the same allocated-couriers list — no extra network
// call. "All Service Types" acts as a select/deselect-all toggle, same as the admin panel.
export function CourierServiceTypeFilter({ couriers = [], parent, label = 'Service Type' }) {
    const { watch, setValue } = useFormContext()
    const serviceTypeValue = watch('serviceType') || []

    const specificOptions = useMemo(() => {
        if (!parent) return []
        const scoped = couriers.filter((c) => c.parent === parent)
        const types = new Set()
        scoped.forEach((c) => {
            if (c.serviceTypePrimary) types.add(c.serviceTypePrimary)
            if (c.serviceTypeSecondary) types.add(c.serviceTypeSecondary)
        })
        return _.orderBy(Array.from(types)).map((t) => ({ value: t, label: t }))
    }, [couriers, parent])

    const options = [ALL_SERVICE_TYPES_OPTION, ...specificOptions]

    const handleChange = (event) => {
        const value = event.target.value
        const prevHadAll = serviceTypeValue.includes(ALL_SERVICE_TYPES)
        const nowHasAll = value.includes(ALL_SERVICE_TYPES)
        if (nowHasAll && !prevHadAll) {
            setValue('serviceType', [ALL_SERVICE_TYPES, ...specificOptions.map((o) => o.value)])
        } else if (!nowHasAll && prevHadAll) {
            setValue('serviceType', [])
        } else if (prevHadAll && value.length < options.length) {
            setValue('serviceType', value.filter((v) => v !== ALL_SERVICE_TYPES))
        } else {
            setValue('serviceType', value)
        }
    }

    return (
        <RHFMultipleSelectField
            name="serviceType"
            label={label}
            width="100%"
            options={options}
            onChange={handleChange}
        />
    )
}

// Resolves a Courier(parent) + Service Type selection down to the specific courier account
// id(s) it matches (comma-joined, for the courierIds bulk-download param). Empty when no
// parent is selected — callers should only send courierIds when this returns non-empty.
export function resolveCourierIdsForParentAndServiceType(couriers = [], parent, serviceTypes) {
    if (!parent) return ''
    const scoped = couriers.filter((c) => c.parent === parent)
    const wantsAll = !serviceTypes || serviceTypes.length === 0 || serviceTypes.includes(ALL_SERVICE_TYPES)
    const matched = wantsAll
        ? scoped
        : scoped.filter((c) => serviceTypes.includes(c.serviceTypePrimary) || serviceTypes.includes(c.serviceTypeSecondary))
    return matched.map((c) => c.id).join(',')
}

// Control Tower / analytics courier filter: cached source, both B2C + B2B,
// shown as a DEDUPED PARENT list (one entry per parent). The value is the
// comma-joined account ids under that parent, so selecting a parent filters
// across all of its courier accounts (matched as a set in the analytics hooks).
export function CourierFilterCached({ enableNoneSelect = true }) {
    const { data } = useGetCourierCached()
    const options = _(data || [])
        .filter((c) => c.parent)
        .groupBy('parent')
        .map((accounts, parent) => ({ value: accounts.map((a) => a.id).join(','), label: parent }))
        .orderBy('label')
        .value()

    return (
        <RHFSelectField
            enableNoneSelect={enableNoneSelect}
            width="100%"
            name="courier"
            label="Select Courier"
            options={options}
        />
    )
}

export function CourierFilterParent({ enableNoneSelect = false }) {
    const { data } = useGetCourier()
    const uniqueOptions = data ? _.uniq(data.map(val => val.parent)) : [];
    return (
        <RHFSelectField
            enableNoneSelect={enableNoneSelect}
            width="100%"
            name="courier"
            label={`Select Courier`}
            options={
                _.orderBy(uniqueOptions, 'name').map((val) => ({
                    value: val,
                    label: val
                }))
            }
        />
    )
}
// Courier dropdown for the Manifest flows (Pending Pickups + Bulk Upload). Unlike the
// allocated-couriers dropdown on the Label/Invoice pages, this lists ONLY the couriers that
// currently have shipments awaiting pickup (un-manifested, pickup-stage B2C orders) -- because
// a manifest can only be generated for such shipments. The label + hint make that explicit so
// sellers know why a courier they use might not appear.
export function ManifestCourierFilter({
    enableNoneSelect = false,
    label = 'Select courier with pending pickups',
    hint = 'Lists only couriers that have shipments awaiting pickup',
}) {
    const [uniqueOptions, setUniqueOptions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCourierData = async () => {
            setLoading(true)
            const defaultFilters = [
                { field: "orderStatus", operator: "in", value: ['ORDER_PLACED', 'PICKUP_PENDING', 'PICKUP_FAILED', 'OUT_FOR_PICKUP', 'AWB_REGISTERED'] },
                { field: "manifestDetails", operator: "exists", value: false },
                { field: "shipmentType", operator: "eq", value: "B2C" }
            ];
            const payload = {
                "andfilter": [...defaultFilters],
                "includes": "courierDetail",
                limit: 5000,
            };
            try {
                const { data } = await postGeneric(apiConstants.ORDER_LISTS, payload);
                const uniqueCouriers = (data.data || []).reduce((acc, item) => {
                    if (item.courierDetail?.key) {
                        const { key, parent, name } = item.courierDetail
                        if (!acc[key]) {
                            acc[key] = { parent, name }
                        }
                    }
                    return acc
                }, {})

                setUniqueOptions(Object.keys(uniqueCouriers).map(key => ({ id: key, parent: uniqueCouriers[key].parent, name: uniqueCouriers[key].name })));
            } catch (error) {
                console.error('Error fetching courier data:', error);
            } finally {
                setLoading(false)
            }
        };
        fetchCourierData();
    }, []);
    return (
        <Box>
            <RHFSelectField
                enableNoneSelect={enableNoneSelect}
                width="100%"
                name="courier"
                label={label}
                noOptionsText={loading ? 'Loading couriers…' : 'No couriers have shipments pending pickup'}
                // Show the specific account/service (e.g. "Ekart — Ekart Small") so sellers can
                // tell apart child couriers under the same parent when generating a manifest.
                options={_.orderBy(uniqueOptions, ['parent', 'name']).map((val) => ({
                    value: val.id,
                    label: val.name && val.name !== val.parent ? `${val.parent} — ${val.name}` : val.parent
                }))}
            />
            {hint && (
                <Typography sx={{ fontSize: 11, color: SUBTLE, mt: 0.5, lineHeight: 1.3, fontStyle: 'italic' }}>
                    {hint}
                </Typography>
            )}
        </Box>
    )
}

export function WhitelistB2BCourier({ id, setSelectedCourier }) {
    const [whitelistCouriers, setWhitelistCouriers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getGeneric(`${apiConstants.MERCHANT_DETAILS}/${id}`)
            .then((response) => {
                if (response?.data?.b2bCheapestCPList) {
                    setWhitelistCouriers(response.data.b2bCheapestCPList);
                } else {
                    setWhitelistCouriers([]);
                }
                setLoading(false);
            })
            .catch((err) => {
                console.error(err);
                setLoading(false);
            });
    }, [id]);

    const handleCourierSelection = (selectedValue) => {
        const selectedCourier = selectedValue;
        setSelectedCourier(selectedCourier);
    };

    return (
        <>
            {loading ? (
                <p>Loading...</p>
            ) : whitelistCouriers.length === 0 ? (
                <p>No couriers available</p>
            ) : (
                <RHFSelectField
                    width="100%"
                    name="courier"
                    label={`Select Courier`}
                    options={whitelistCouriers.map((val) => ({
                        value: val,
                        label: val,
                    }))
                    }
                    onChange={(event) => {
                        handleCourierSelection(event.target.value);
                    }}
                />
            )}
        </>
    );
}

export function CourierList({ filterList, setSelectedCourier }) {
    const andfilter = [{ "field": "is_deleted", "operator": "eq", "value": '0' }];
    const filter = {
        "andfilter": andfilter,
        "offset": 0,
        "limit": 500,
        // "controllerFilter": true,
        "skipCount": true,
        "includes": "id,name,cpAccountCode,prozoId,supportedShipmentType,isBlended"
        // "excludes": exclude,
    }
    const { data } = useGetAllCourierPartnersQuery(filter);
    const filteredData = filterList ? data?.filter(val => val.supported_shipment === filterList) : null;
    // used for bulk allocation
    const handleCourierSelection = (selectedValue) => {
        const selectedOption = _.orderBy(filteredData, 'value').find(val => val.id === selectedValue);
        setSelectedCourier(selectedOption.prozoId);
    };
    return (
        <>
            {filteredData && setSelectedCourier ? (
                <RHFSelectField
                    width="100%"
                    name="courier"
                    label={`Select Courier`}
                    options={_.orderBy(filteredData, 'name').map(val => ({
                        value: val.id,
                        label: val.cpAccountCode + " (" + val.prozoId + ")",
                        additionalData: val.prozoId
                    }))}
                    onChange={(event) => { handleCourierSelection(event.target.value) }}
                />
            ) : filteredData && !setSelectedCourier ? (
                <RHFSelectField
                    width="100%"
                    name="courier"
                    label={`Select Courier`}
                    options={_.orderBy(filteredData, 'name').map(val => ({
                        value: val.id,
                        label: val.cpAccountCode + " (" + val.prozoId + ")",
                    }))}
                />
            ) : (
                <RHFSelectField
                    width="100%"
                    name="courier"
                    label={`Select Courier`}
                    options={_.orderBy(data, 'name').map(val => ({
                        value: val.id,
                        label: val.cpAccountCode + " (" + val.prozoId + ")",
                    }))}
                />
            )}
        </>
    );
}
